<template>
  <form v-if="firm" class="ui-panel ui-form" novalidate @submit.prevent="save">
    <ui-banner v-if="errorCount" ref="banner" role="alert" tabindex="-1">{{ rest || $t("delivery_firms.form.errors", { count: errorCount }) }}</ui-banner>

    <ui-banner v-if="!link" tone="info" icon="mdi-information-outline">{{ $t("delivery_firms.coop.new_note", { restaurant: restaurantName }) }}</ui-banner>

    <ui-confirm v-if="confirming" :title="$t('currency_mismatch_title')">
      {{ $t("currency_mismatch_warning", { restaurant: restaurantName, restaurantCurrency: restaurantCurrencyText, companyCurrency: firm.currency }) }}
      <template #actions>
        <ui-button variant="dark" size="sm" :disabled="saving" @click="saveAnyway">{{ $t("delivery_firms.coop.save_anyway") }}</ui-button>
        <ui-button size="sm" @click="confirming = false">{{ $t("cancel") }}</ui-button>
      </template>
    </ui-confirm>

    <ui-section :title="$t('delivery_firms.sections.status')">
      <ui-check v-model="form.activeRestaurant">{{ $t("status_restoran") }}</ui-check>
      <ui-check v-model="form.activeCompany" :hint="$t('delivery_firms.switch.hint')">{{ $t("status_company") }}</ui-check>
      <ui-check v-model="form.internal" :hint="$t('delivery_firms.coop.internal_hint')">{{ $t("internal") }}</ui-check>
    </ui-section>

    <ui-section :title="$t('delivery_firms.sections.payment')">
      <div class="ui-grid">
        <ui-field v-model="form.payType" name="coop-pay-type" :label="$t('pay_type')" :error="err('payType')" :options="payOptions" />
        <ui-field v-model="form.paying" name="coop-paying" :label="`${$t('amount')} (${suffix})`" :error="err('paying')" inputmode="decimal" />
      </div>
    </ui-section>

    <div class="ui-form-actions">
      <ui-button :disabled="saving" @click="cancel">{{ $t("cancel") }}</ui-button>
      <ui-button type="submit" variant="primary" :disabled="saving">{{ $t("save") }}</ui-button>
    </div>
  </form>
</template>

<script>
import { getRestaurantCurrency, hasCurrencyMismatch } from "@/utils/currency";
import { LINK_PAYING_TYPES, formFromLink, validateLinkForm } from "../firmsLogic";
import hasFirmLabels from "../hasFirmLabels";

/**
 * Saradnja izabranog restorana sa firmom (zamjena za dijalog "Podešavanja dostave"): statusi, interna firma, način i iznos plaćanja.
 * Šalje se samo red ovog restorana i id firme. Neusklađena valuta traži potvrdu u samom panelu, kao u starom dijalogu.
 */
export default {
  name: "CooperationPanel",
  mixins: [hasFirmLabels],
  inject: ["reportShell"],
  props: { firmId: { type: Number, required: true } },
  data() {
    const shell = this.reportShell;
    const firm = shell.flat.find((item) => item.id === this.firmId) || null;
    const link = firm ? shell.linkOf(firm) : null;
    return {
      form: formFromLink(link),
      errors: {},
      rest: "",
      saving: false,
      confirming: false,
    };
  },
  computed: {
    shell() {
      return this.reportShell;
    },
    firm() {
      return this.shell.flat.find((item) => item.id === this.firmId) || null;
    },
    link() {
      return this.firm ? this.linkOf(this.firm) : null;
    },
    errorCount() {
      return Object.keys(this.errors).length || (this.rest ? 1 : 0);
    },
    restaurantName() {
      const restaurant = this.$store.state.restaurant;
      return this.toLatin((restaurant && restaurant.name) || "");
    },
    restaurantCurrencyText() {
      return getRestaurantCurrency(this.$store.state.restaurant);
    },
    mismatch() {
      return (
        Boolean(this.firm) &&
        hasCurrencyMismatch(this.$store.state.restaurant, this.firm.raw)
      );
    },
    payOptions() {
      return [{ value: "", label: this.$t("delivery_firms.form.not_selected") }].concat(
        LINK_PAYING_TYPES.map((value) => ({
          value: String(value),
          label: this.payTypeLabel(value),
        }))
      );
    },
    suffix() {
      return String(this.form.payType) === "2"
        ? "%"
        : this.firm
        ? this.firmCurrency(this.firm)
        : "KM";
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        Object.keys(this.errors).forEach((key) => {
          if (this.touched[key] !== this.form[key]) {
            this.$delete(this.errors, key);
          }
        });
      },
    },
  },
  created() {
    this.touched = {};
  },
  methods: {
    err(key) {
      const code = this.errors[key];
      if (!code) return "";
      return code === "number" ? this.$t("delivery_firms.form.number") : code;
    },
    async save() {
      if (this.saving) return;
      this.rest = "";
      const local = validateLinkForm(this.form);
      if (Object.keys(local).length) {
        this.showErrors(local);
        return;
      }
      if (this.mismatch && !this.confirming) {
        this.confirming = true;
        return;
      }
      await this.submit();
    },
    async saveAnyway() {
      this.confirming = false;
      await this.submit();
    },
    async submit() {
      this.saving = true;
      try {
        const result = await this.shell.submitCooperation(this.form, this.firm);
        if (
          result &&
          (result.rest || (result.fields && Object.keys(result.fields).length))
        ) {
          this.rest = result.rest || "";
          this.showErrors(result.fields || {});
        }
      } finally {
        this.saving = false;
      }
    },
    showErrors(errors) {
      this.touched = {};
      Object.keys(errors).forEach((key) => {
        this.touched[key] = this.form[key];
      });
      this.errors = { ...errors };
      this.$nextTick(() => {
        const first = this.$el.querySelector("[aria-invalid='true']");
        if (first) first.focus();
        else if (this.$refs.banner) this.$refs.banner.$el.focus();
      });
    },
    cancel() {
      this.shell.openFirm(this.firmId);
    },
  },
};
</script>
